import { inject, Injectable } from '@angular/core';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';
import { Observable } from 'rxjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface SalesBookClient {
  id: string;
  email: string;
  username: string;
  /** Client details shown by the admin user cell (same as order / trade rows). */
  userInfo: {
    brokerageAccount: string;
    firstName: string;
    lastName: string;
    companyName: string;
    email: string;
    phone: string;
    dateOfBirth: string | null;
  } | null;
  kycStatus: string;
  created: string;
  managerId: string;
  totalUsd: number;
  portfolioMarketValueUsd: number;
  fiatUsd: number;
  /** Valuation failed on the backend; the client counts as 0 in the totals. */
  valuationError: boolean;
}

export interface SalesBook {
  managerId: string;
  clientCount: number;
  totalPortfolioUsd: number;
  clients: SalesBookClient[];
}

export interface SalesManagerBook {
  id: string;
  email: string;
  username: string;
  name: string;
  role: string;
  clientCount: number;
  totalPortfolioUsd: number;
  clients: SalesBookClient[];
}

export interface SalesManagersOverview {
  managerCount: number;
  totalClients: number;
  totalPortfolioUsd: number;
  managers: SalesManagerBook[];
}

export interface BankParty {
  name: string;
  address: string;
  swiftCode: string;
  /** 9-digit Russian БИК — a different code from swiftCode, both may be set */
  bicCode: string;
  accountNumber: string;
}

/**
 * The beneficiary on a bank record is not necessarily the account owner, so
 * these identifiers are unrelated to the user profile's KYC ssn / kpp.
 */
export interface BankBeneficiaryCustomer {
  name: string;
  accountNumber: string;
  ssn: string;
  kppCode: string;
}

export interface BankCredential {
  currency: string;
  correspondentBank: BankParty;
  beneficiaryBank: BankParty;
  beneficiaryCustomer: BankBeneficiaryCustomer;
  paymentPurpose: string;
}

/**
 * When a user picks "Other bank" and then one of these currencies, this bank's
 * credentials are shown. Every currency here must also exist in credentials[],
 * and a currency may be claimed by only one bank.
 */
export interface BankOther {
  currencies: string[];
}

/** Read model. `icon` is an absolute S3 URL; writes take an object key instead. */
export interface Bank {
  id: string;
  name: string;
  icon: string | null;
  hidden: boolean;
  other: BankOther | null;
  credentials: BankCredential[];
  removed?: boolean;
}

export interface BankVariable {
  key: string;
  label: string;
  value: string;
}

export interface BanksResponse {
  variables: BankVariable[];
  banks: Bank[];
}

/**
 * Write model. The backend validator rejects unknown keys ("Unexpected parameter
 * <key>"), so never spread the read model into this. On PUT, an omitted icon /
 * hidden / other is PRESERVED; name and credentials are required every time and
 * replace the stored values wholesale.
 */
export interface BankWritePayload {
  name: string;
  credentials: BankCredential[];
  icon?: string;
  hidden?: boolean;
  other?: BankOther | null;
}

/**
 * Bank withdrawal created by an operator on a user's behalf. Field-for-field the
 * same contract the mobile app posts (`BankWithdrawalDetailsDto` in
 * `trading-mobile/lib/modules/dashboard/money_moving/models/withdraw_bank_request.dart`),
 * so a request made here is indistinguishable downstream from a client-made one.
 *
 * Every optional key must be OMITTED when blank — the backend checks each
 * optional field it is given, and an empty string is not the same as an absent
 * one. `swift` and `bicCode` are fed from a single SWIFT/BIC input and carry the
 * same value, exactly as mobile sends them.
 */
export interface AdminBankWithdrawalDetails {
  bankName: string;
  bankAccount: string;
  accountHolder: string;
  /** ISO alpha-2, e.g. `AM` — not the display name. */
  bankCountry: string;
  swift?: string;
  bicCode?: string;
  beneficiaryAddress?: string;
  /** Russia: ИНН. Required only for RUB to a Russian bank. */
  ssn?: string;
  kppCode?: string;
  correspondentBankName?: string;
  correspondentBankAccount?: string;
  correspondentBankSwift?: string;
  correspondentBankAddress?: string;
  /** Payment purpose, ≤140 chars. */
  paymentDetails?: string;
}

export interface AdminBankWithdrawRequest {
  userId: string;
  /**
   * Must be a fiat symbol: the route validates `fiatCurrency`, because a bank
   * withdrawal writes a `bank_withdraw` ledger and the shared confirmation path
   * would fire an on-chain transfer for a crypto symbol.
   */
  currency: string;
  /** A number, never a string. */
  amount: number;
  bankWithdrawalDetails: AdminBankWithdrawalDetails;
  /**
   * Defaults to TRUE server-side, and that default is the one that does not
   * ask the client: the withdrawal is created ALREADY CONFIRMED — the money is
   * debited and the row lands in the payouts queue at `accepted`.
   *
   * Pass `false` to ask the client instead: status `new` (frozen, not
   * debited), a push notification goes out, and the client settles it via
   * POST /user/confirm-admin-withdraw/:id or cancels it.
   */
  skipConfirmation?: boolean;
}

/**
 * `GET /admin/withdraw/payment-purpose` — the default payment purpose in every
 * language, the client's brokerage account already appended.
 */
export interface AdminWithdrawPaymentPurposes {
  en: string;
  ru: string;
  hy: string;
}

/** `GET /admin/withdraw/max` — the largest amount this user can withdraw. */
export interface AdminWithdrawMaxQuery {
  userId: string;
  currency: string;
  /** Changes the answer: the fee differs by destination country. */
  bankCountry?: string;
}

/**
 * A payee the client has saved. The backend has shipped both a flat shape and
 * one nesting the bank fields under `bankWithdrawalDetails`, and the account
 * number has travelled under three different keys, so every field a picker
 * reads is optional here and resolved through {@link savedPayeeField}.
 */
export interface AdminSavedBankingDetails {
  id: string;
  currency: string;
  bankName?: string;
  bankAccount?: string;
  accountNumber?: string;
  iban?: string;
  accountHolder?: string;
  beneficiaryName?: string;
  beneficiaryAddress?: string;
  bankCountry?: string;
  swift?: string;
  bicCode?: string;
  effectiveBicCode?: string;
  ssn?: string;
  kppCode?: string;
  correspondentBankName?: string;
  correspondentBankAccount?: string;
  correspondentBankSwift?: string;
  correspondentBankAddress?: string;
  bankWithdrawalDetails?: Partial<AdminBankWithdrawalDetails>;
}

export interface AdminWithdrawEstimate {
  fee: number;
  managerFee: number;
  currency: string;
  percent: number;
  /** Amount the beneficiary receives. */
  amount: number;
  /** Amount debited from the wallet: what the available-balance check uses. */
  totalCharged: number;
}

export interface AdminSecuritiesWithdrawDetails {
  brokerName: string;
  brokerAccount: string;
  accountHolder: string;
  brokerCountry: string;
  transferRef?: string;
  /** YYYY-MM-DD */
  transferDate?: string;
}

export interface AdminSecuritiesWithdrawRequest {
  userId: string;
  /** Instrument balance key, `${tickerId}~${sourceId}`. */
  currency: string;
  quantity: number;
  securitiesWithdrawDetails: AdminSecuritiesWithdrawDetails;
  note?: string;
  syncWith1C?: boolean;
  /**
   * Admin-set fee in the instrument's price currency. Omit it to charge the
   * configured fee from {@link AdminService.estimateSecuritiesWithdraw}.
   */
  fee?: number;
}

export interface AdminSecuritiesWithdrawEstimate {
  fee: number;
  /** Recalculated from `fee` when the admin overrides it (percent-of-fee mode). */
  managerFee: number;
  /** The instrument's price currency; `fee` is denominated in it. */
  currency: string;
  percent: number;
  min: number | null;
  max: number | null;
  price: number;
  quantity: number;
  notional: number;
}

/**
 * Reads one logical field off a saved payee, tolerating both the flat and the
 * nested shape and the key aliases the account number has travelled under.
 * Returns '' rather than undefined so callers can assign straight into a form.
 */
export function savedPayeeField(
  payee: AdminSavedBankingDetails | null | undefined,
  field: keyof AdminBankWithdrawalDetails
): string {
  if (!payee) return '';

  const nested = payee.bankWithdrawalDetails ?? {};
  const aliases: Partial<Record<keyof AdminBankWithdrawalDetails, string[]>> = {
    bankAccount: ['bankAccount', 'iban', 'accountNumber'],
    accountHolder: ['accountHolder', 'beneficiaryName'],
    // `effectiveBicCode` is the client app's resolved value: bicCode when set,
    // otherwise swift. Preferring it keeps the two pickers in agreement.
    bicCode: ['effectiveBicCode', 'bicCode', 'swift'],
    swift: ['swift', 'effectiveBicCode', 'bicCode'],
  };

  const keys = aliases[field] ?? [field];
  for (const key of keys) {
    const value =
      (nested as Record<string, unknown>)[key] ??
      (payee as unknown as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

export interface Custodian {
  id: string;
  code: string;
  name: string;
  oneCDepository: string;
  active: boolean;
  isSystem: boolean;
  created?: string;
  updated?: string;
}

export interface CustodyRow {
  custodian: string;
  quantity: number;
  frozen: number;
  available: number;
}

export interface UserCustody {
  currency: string;
  total: number;
  frozen: number;
  gtn: { quantity: number; frozen: number; available: number };
  custodians: CustodyRow[];
  logs?: {
    id: string;
    before: { custodian: string; quantity: number }[];
    after: { custodian: string; quantity: number }[];
    reason: 'admin_edit' | 'instrument_conversion' | 'bond_maturity' | 'gtn_sync';
    adminId: string | null;
    note: string;
    ref: string | null;
    created: string;
  }[];
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);

  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  searchUser(
    query: any,
    page: number,
    itemsPerPage: number,
    sortBy: string
  ): Observable<any> {
    query.page = page;
    query.itemsPerPage = itemsPerPage;
    query.sortBy = sortBy;
    if (query.status === 'any') {
      delete query.status;
    }
    if (query.isBlocked === 'any') {
      delete query.isBlocked;
    }
    if (query.kycStatus === 'any') {
      delete query.kycStatus;
    }
    if (query.sumsubReviewStatus === 'any') {
      delete query.sumsubReviewStatus;
    }
    if (query.role === 'any') {
      delete query.role;
    }
    if (query.search === '') {
      delete query.search;
    }
    if (query.riskLevel === 'any') {
      delete query.riskLevel;
    }
    if (query.userRiskLevel === 'any') {
      delete query.userRiskLevel;
    }
    if (query.sanctionRiskLevel === 'any') {
      delete query.sanctionRiskLevel;
    }
    if (query.hasManager === 'any') {
      delete query.hasManager;
    }
    if (query.isRussianPerson === 'any') {
      delete query.isRussianPerson;
    }
    if (!query.onlyNegativeBalance) {
      delete query.onlyNegativeBalance;
    }

    return this.networkHelper.post(`${this.hostUrl}/user/search`, query);
  }

  searchUserRef(
    query: any,
    userId: string,
    page: number,
    itemsPerPage: number,
    sortBy: string
  ): Observable<any> {
    query.page = page;
    query.itemsPerPage = itemsPerPage;
    query.referrerId = userId;
    query.sortBy = sortBy;

    if (query.status === 'any') {
      delete query.status;
    }
    if (query.isBlocked === 'any') {
      delete query.isBlocked;
    }
    if (query.search === '') {
      delete query.search;
    }

    return this.networkHelper.post(`${this.hostUrl}/user/search`, query);
  }

  createWaitlistUser(payload: any) {
    return this.networkHelper.post(`${this.hostUrl}/admin/user`, payload);
  }

  generateBrokerageAccount(
    isResident: boolean,
    accountType: string,
    legalEntityCategory: number
  ): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/generate-brokerage-account`,
      {
        isResident,
        accountType,
        legalEntityCategory,
      }
    );
  }

  generateContractNumber(): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/generate-contract-number`
    );
  }

  addNewReferrer(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/add-referrer`, data);
  }

  getGroup(type: string): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/admin/group/${type}`);
  }

  addGroup(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/add-group`, data);
  }

  adjust(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/adjustment`, data);
  }

  /**
   * `syncWith1C` (optional, defaults to `true` server-side) decides whether the
   * ledger row this creates is queued for the 1C exporter. Send `false` only
   * when the movement did not originate here — 1C already has it from a bank
   * feed, a depository file, or a hand entry by the accountants — otherwise it
   * is double-counted. The flag is read once, at creation: `confirmDeposit`
   * does not touch it and there is no endpoint to change it afterwards.
   *
   * It marks the parent ledger only. The `fee` child row raised alongside a
   * securities deposit/withdraw is always excluded from the queue, and the
   * manager `sales_fee` row does not inherit the flag.
   */
  addBankDeposit(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/deposit/bank`, data);
  }

  /**
   * Accepts `syncWith1C` — see {@link AdminService.addBankDeposit} — and an
   * optional `valueDate` (date-time with offset) that backdates the ledger.
   */
  addBondCoupon(data: any): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/deposit/bond-coupon`,
      data
    );
  }

  /**
   * Accepts `syncWith1C` — see {@link AdminService.addBankDeposit} — and an
   * optional `valueDate` (date-time with offset) that backdates the ledger.
   */
  addDividend(data: any): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/deposit/dividend`,
      data
    );
  }

  /**
   * Accepts `syncWith1C` — see {@link AdminService.addBankDeposit} — and an
   * optional `valueDate` (date-time with offset) that backdates the ledger.
   */
  addFee(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/deposit/fee`, data);
  }

  confirmBankDeposit(ledgerId: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/deposit/confirm`, {
      ledgerId,
    });
  }

  confirmSecuritiesDeposit(ledgerId: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/deposit/confirm`, {
      ledgerId,
    });
  }

  cancelDeposit(ledgerId: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/deposit/cancel`, {
      ledgerId,
    });
  }

  /** Accepts `syncWith1C` — see {@link AdminService.addBankDeposit}. */
  addSecuritiesDeposit(data: any): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/deposit/securities`,
      data
    );
  }

  /** Accepts `syncWith1C` — see {@link AdminService.addBankDeposit}. */
  addSecuritiesWithdraw(data: AdminSecuritiesWithdrawRequest): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/withdraw/securities`,
      data
    );
  }

  /**
   * Fee preview for {@link AdminService.addSecuritiesWithdraw}, priced for
   * `userId` rather than the caller.
   */
  estimateSecuritiesWithdraw(
    data: Pick<
      AdminSecuritiesWithdrawRequest,
      'userId' | 'currency' | 'quantity'
    >
  ): Observable<AdminSecuritiesWithdrawEstimate> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/withdraw/securities/estimate`,
      data
    );
  }

  /**
   * Raises a bank withdrawal for a client from the admin panel.
   *
   * Unlike the neighbouring /admin/deposit/* routes this one is ADMIN_ROLES
   * only and carries no `admin:balance` API-key scope, so an API key cannot
   * move client money off the platform.
   *
   * Read {@link AdminBankWithdrawRequest.skipConfirmation} before calling:
   * the default debits the client immediately without asking them.
   *
   * The route's Params() is a strict allow-list — `userId`, `currency`,
   * `amount`, `bankWithdrawalDetails`, `skipConfirmation` and nothing else.
   * It does NOT take `syncWith1C` or a note, unlike /admin/deposit/bank; adding
   * one is rejected outright with "Unexpected parameter <key>".
   */
  addBankWithdraw(data: AdminBankWithdrawRequest): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/withdraw/bank`, data);
  }

  /**
   * Fee preview for {@link AdminService.addBankWithdraw}, priced for `userId`
   * rather than the caller: the fee resolves the target's group and manager
   * fee, so the client-side estimate would quote the acting admin's own group.
   *
   * Takes the create body minus `skipConfirmation`.
   */
  estimateBankWithdraw(
    data: Omit<AdminBankWithdrawRequest, 'skipConfirmation'>
  ): Observable<AdminWithdrawEstimate> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/withdraw/bank/estimate`,
      data
    );
  }

  /** The largest amount `userId` can withdraw, fees already taken off. */
  getBankWithdrawMax(params: AdminWithdrawMaxQuery): Observable<any> {
    const query: Record<string, string> = {
      userId: params.userId,
      currency: params.currency,
    };
    if (params.bankCountry) {
      query['bankCountry'] = params.bankCountry;
    }
    return this.networkHelper.get(`${this.hostUrl}/admin/withdraw/max`, query);
  }

  /** Default payment purposes for `userId`'s bank withdrawals. */
  getWithdrawPaymentPurpose(
    userId: string
  ): Observable<AdminWithdrawPaymentPurposes> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/withdraw/payment-purpose`,
      { userId }
    );
  }

  /**
   * The client's saved payees, for prefilling the withdrawal form. A separate
   * route from the client's own /user-bank precisely so that one stays scoped
   * to its session.
   */
  getUserBanksForAdmin(
    userId: string,
    currency?: string
  ): Observable<AdminSavedBankingDetails[]> {
    const query: Record<string, string> = { userId };
    if (currency) {
      query['currency'] = currency;
    }
    return this.networkHelper.get(`${this.hostUrl}/admin/user-bank`, query);
  }

  getPendingLedger(userId: string, source: string): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/user/transactions`, {
      userId,
      type: source,
      status: 'new',
      includePending: true,
      page: 1,
      itemsPerPage: 10,
    });
  }

  getTradesAmount(
    startDate: number,
    endDate: number,
    symbol?: string
  ): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    if (symbol && symbol.toLowerCase() !== 'any') {
      query['symbol'] = symbol;
    }

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(`${this.hostUrl}/admin/trades-amount`, query);
  }

  getTradesFees(
    startDate: number,
    endDate: number,
    symbol?: string
  ): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    if (symbol && symbol.toLowerCase() !== 'any') {
      query['symbol'] = symbol;
    }

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(`${this.hostUrl}/admin/trades-fees`, query);
  }

  getTradesCount(
    startDate: number,
    endDate: number,
    symbol?: string
  ): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    if (symbol && symbol.toLowerCase() !== 'any') {
      query['symbol'] = symbol;
    }

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(`${this.hostUrl}/admin/trades-count`, query);
  }

  tradesCountAllCsv(startDate: Date, endDate: Date): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(
      `${this.hostUrl}/admin/csv/trades-count-all`,
      query
    );
  }

  tradesAmountAllCsv(startDate: number, endDate: number): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(
      `${this.hostUrl}/admin/csv/trades-amount-all`,
      query
    );
  }

  tradesFeesAllCsv(startDate: number, endDate: number): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(
      `${this.hostUrl}/admin/csv/trades-fees-all`,
      query
    );
  }

  depositFeesAllCsv(startDate: number, endDate: number): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(
      `${this.hostUrl}/admin/csv/deposit-fees-all`,
      query
    );
  }

  withdrawFeesAllCsv(startDate: number, endDate: number): Observable<any> {
    const query: any = {
      startDate: startDate,
      endDate: endDate,
    };

    query['timeZoneShift'] = dayjs().utcOffset();
    return this.networkHelper.get(
      `${this.hostUrl}/admin/csv/withdraw-fees-all`,
      query
    );
  }

  getSummary(symbol?: string): Observable<any> {
    const query: any = {
      timeZoneShift: dayjs().utcOffset(),
    };

    if (symbol && symbol.toLowerCase() !== 'any') {
      query.symbol = symbol;
    }

    return this.networkHelper.get(`${this.hostUrl}/admin/summary`, query);
  }

  getPayouts(
    status: string,
    currency: string,
    page: number,
    itemPerPage: number,
    startDate: number,
    endDate: number,
    search: string
  ): Observable<any> {
    const query: any = {
      status: status,
      currency: currency,
      page: page,
      itemsPerPage: itemPerPage,
      startDate: startDate,
      endDate: endDate,
      search: search ? search : '',
    };

    return this.networkHelper.get(`${this.hostUrl}/admin/payouts`, query);
  }

  getPendingPayoutsCount(startDate: number): Observable<any> {
    const query: any = {
      startDate: startDate,
    };
    return this.networkHelper.get(
      `${this.hostUrl}/admin/pending-payouts-count`,
      query
    );
  }

  getPayoutsCsv(
    status: string,
    currency: string,
    startDate: number,
    endDate: number
  ): Observable<any> {
    const query: any = {
      status: status,
      currency: currency,
      startDate: startDate,
      endDate: endDate,
      timeZoneShift: dayjs().utcOffset(),
    };

    return this.networkHelper.get(`${this.hostUrl}/admin/csv/payouts`, query);
  }

  getBalancesCsv(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/admin/csv/balances`, {});
  }

  getUsersCsv(query: any): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/user/csv/search`,
      this.usersCsvQuery(query)
    );
  }

  /** Same users and filters as getUsersCsv, one row per non-zero balance. */
  getUsersBalancesCsv(query: any): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/user/csv/search-balances`,
      this.usersCsvQuery(query)
    );
  }

  /** Users CSV filters without the unset ones ("any" sentinels, empty search strings). */
  private usersCsvQuery(query: any): any {
    const queryParams = { ...query };

    for (const key of [
      'status',
      'isBlocked',
      'kycStatus',
      'sumsubReviewStatus',
      'termsAccepted',
      'hasManager',
      'isRussianPerson',
    ]) {
      if (queryParams[key] === 'any') {
        delete queryParams[key];
      }
    }
    if (queryParams.search === '') {
      delete queryParams.search;
    }

    return queryParams;
  }

  /**
   * `executionDate` - when the bank transfer actually went out, as an ISO date-time
   * with offset. Required by the backend for bank (fiat) payouts; sent to 1C as the
   * operation date.
   */
  approvePayout(
    payoutId: string,
    referenceNumber?: string,
    executionDate?: string
  ): Observable<any> {
    const body: any = {};
    if (referenceNumber) body.referenceNumber = referenceNumber;
    if (executionDate) body.executionDate = executionDate;
    return this.networkHelper.post(
      `${this.hostUrl}/admin/payouts/${payoutId}`,
      body
    );
  }

  rejectPayout(payoutId: string, reason: string): Observable<any> {
    return this.networkHelper.patch(
      `${this.hostUrl}/admin/payouts/${payoutId}`,
      { reason: reason }
    );
  }

  failedPayout(payoutId: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/payouts/${payoutId}`,
      {}
    );
  }

  refundPayout(payoutId: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/payouts-refund/${payoutId}`,
      {}
    );
  }

  updatePayoutStatus(payoutId: string, status?: any, txid?: any) {
    const body: any = {};
    if (status) {
      body.status = status;
    }
    if (txid) {
      body.txid = txid;
    }

    return this.networkHelper.put(
      `${this.hostUrl}/admin/payouts/${payoutId}`,
      body
    );
  }

  /** PUT /api/user/:id/risk-levels — compliance risk and sanction risk (low/medium/high). */
  setUserRiskLevels(
    userId: string,
    levels: { riskLevel?: string; sanctionRiskLevel?: string }
  ): Observable<{ riskLevel: string; sanctionRiskLevel: string }> {
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/risk-levels`,
      levels
    );
  }

  blockUserWithdrawal(userId: string, reason: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/block-withdrawal`,
      { reason }
    );
  }

  unblockUserWithdrawal(userId: string, reason: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/unblock-withdrawal`,
      { reason }
    );
  }

  blockUserTrading(userId: string, reason: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/block-trading`,
      { reason }
    );
  }

  unblockUserTrading(userId: string, reason: string): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/unblock-trading`,
      { reason }
    );
  }

  setRussianPerson(userId: string, isRussianPerson: boolean): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/russian-person`,
      { isRussianPerson }
    );
  }

  setUserRole(
    userId: string,
    role: 'user' | 'sales-manager' | 'sales-supervisor'
  ): Observable<any> {
    return this.networkHelper.patch(`${this.hostUrl}/user/${userId}/role`, {
      role,
    });
  }

  getSalesManagers(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/user/sales-managers`);
  }

  /** The logged-in sales manager's / supervisor's own clients, valued in USD. */
  getMySalesBook(): Observable<SalesBook> {
    return this.networkHelper.get<SalesBook>(`${this.hostUrl}/user/sales/my-book`);
  }

  /** A given sales manager's / supervisor's book (their admin user page). */
  getUserSalesBook(managerId: string): Observable<SalesBook> {
    return this.networkHelper.get<SalesBook>(`${this.hostUrl}/user/${managerId}/sales-book`);
  }

  /** Every sales manager with their book (sales-supervisor only). */
  getSalesManagersOverview(): Observable<SalesManagersOverview> {
    return this.networkHelper.get<SalesManagersOverview>(
      `${this.hostUrl}/user/sales/managers-overview`
    );
  }

  setUserManager(
    userId: string,
    managerId: string | null,
    reason: string
  ): Observable<any> {
    const payload: any = { reason };
    if (managerId) {
      payload['managerId'] = managerId;
    }
    return this.networkHelper.put(
      `${this.hostUrl}/user/${userId}/manager`,
      payload
    );
  }

  getUserManagerHistory(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/user/${userId}/manager-history`
    );
  }

  getTradesHistory(
    symbol: string,
    side: string,
    page: number,
    itemsPerPage: number,
    startDate: number,
    endDate: number,
    containTotals: boolean,
    keyword?: string | null
  ): Observable<any> {
    const query: any = {
      side: side,
      page: page,
      itemsPerPage: itemsPerPage,
      startDate: startDate,
      endDate: endDate,
      containTotals: containTotals,
    };

    // Free-form: an ObjectId, an email fragment, or a CUB... brokerage account.
    // Omitted entirely when unset — the param has no default server-side.
    if (keyword) {
      query.keyword = keyword;
    }

    if (symbol && symbol.toLowerCase() !== 'any') {
      query.symbol = symbol;
    }

    return this.networkHelper.get(`${this.hostUrl}/admin/trades-all`, query);
  }

  getTradesHistoryCsv(
    symbol: string,
    side: string,
    startDate: number,
    endDate: number,
    keyword?: string | null
  ): Observable<any> {
    const query: any = {
      side: side,
      startDate: startDate,
      endDate: endDate,
      timeZoneShift: dayjs().utcOffset(),
    };

    if (keyword) {
      query.keyword = keyword;
    }

    if (symbol && symbol.toLowerCase() !== 'any') {
      query.symbol = symbol;
    }

    return this.networkHelper.get(
      `${this.hostUrl}/admin/csv/trades-all`,
      query
    );
  }

  clearTelegramId(userId: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/user/remove-telegram`, {
      userId: userId,
    });
  }

  getUserLedgers(userId: string): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/admin/csv/ledgers`, {
      userId,
    });
  }

  searchInstruments(params: {
    keyword?: string;
    currency?: string;
    country?: string;
    sourceId?: string;
    status?: string;
    assetClassId?: string;
    isOtc?: boolean;
    isPresale?: boolean;
    page: number;
    itemsPerPage: number;
  }): Observable<any> {
    const query: any = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.isOtc !== undefined) {
      query.isOtc = params.isOtc;
    }
    // Booleans need an explicit undefined check: `isPresale: false` is a real
    // filter (listed instruments) that a truthiness guard would silently drop.
    if (params.isPresale !== undefined) {
      query.isPresale = params.isPresale;
    }

    if (params.keyword) {
      query.keyword = params.keyword;
    }
    if (params.currency) {
      query.currency = params.currency;
    }
    if (params.country) {
      query.country = params.country;
    }
    if (params.sourceId) {
      query.sourceId = params.sourceId;
    }
    if (params.status) {
      query.status = params.status;
    }
    if (params.assetClassId) {
      query.assetClassId = params.assetClassId;
    }

    return this.networkHelper.get(`${this.hostUrl}/instrument/search`, {
      ...query,
    });
  }

  getInstrumentByInternalId(instrumentInternalId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/instrument/${encodeURIComponent(instrumentInternalId)}`
    );
  }

  createInstrument(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/instrument`, data);
  }

  updateInstrument(id: string, data: any): Observable<any> {
    return this.networkHelper.put(`${this.hostUrl}/instrument/${id}`, data);
  }

  // OTC Source API methods
  getOtcSources(params?: {
    name?: string;
    sourceId?: string;
    provider?: string;
    status?: 'active' | 'inactive';
  }): Observable<any> {
    const query: any = {};
    if (params?.name) query.name = params.name;
    if (params?.sourceId) query.sourceId = params.sourceId;
    if (params?.provider) query.provider = params.provider;
    if (params?.status) query.status = params.status;
    return this.networkHelper.get(
      `${this.hostUrl}/instrument/otc-source`,
      query
    );
  }

  createOtcSource(data: {
    name: string;
    sourceId: string;
    active: boolean;
    provider?: string;
    aciRounding?: 'per_position' | 'per_unit';
  }): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/instrument/otc-source`,
      data
    );
  }

  updateOtcSource(
    id: string,
    data: { name: string; active: boolean; provider?: string; aciRounding?: 'per_position' | 'per_unit' }
  ): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/instrument/otc-source/${id}`,
      data
    );
  }

  // Custodian API methods (trading-be-1/docs/custodians.md)
  getCustodians(params?: {
    code?: string;
    name?: string;
    status?: 'active' | 'inactive';
  }): Observable<Custodian[]> {
    const query: any = {};
    if (params?.code) query.code = params.code;
    if (params?.name) query.name = params.name;
    if (params?.status) query.status = params.status;
    return this.networkHelper.get(`${this.hostUrl}/admin/custodians`, query);
  }

  createCustodian(data: {
    code: string;
    name: string;
    oneCDepository?: string;
    active: boolean;
  }): Observable<Custodian> {
    return this.networkHelper.post(`${this.hostUrl}/admin/custodians`, data);
  }

  /** `code` is immutable server-side and is not sent. */
  updateCustodian(
    id: string,
    data: { name?: string; oneCDepository?: string; active?: boolean }
  ): Observable<Custodian> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/custodians/${id}`,
      data
    );
  }

  /** Per-custodian split of one instrument holding, plus the edit history. */
  getUserCustody(userId: string, currency: string): Observable<UserCustody> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/balances/${userId}/custody/${encodeURIComponent(
        currency
      )}`
    );
  }

  /**
   * Sets every non-GTN custodian's quantity; GTN holds the rest. The total
   * does not change.
   */
  setUserCustody(
    userId: string,
    data: {
      currency: string;
      custody: { custodian: string; quantity: number }[];
      note?: string;
    }
  ): Observable<UserCustody> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/balances/${userId}/custody`,
      data
    );
  }

  getInstrumentUpdateInfo(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/instrument/update-info`);
  }

  updateTickers(): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/instrument/update-tickers`,
      {}
    );
  }

  // Bank API methods

  /** Returns every non-removed bank, hidden ones included — the backend no
   *  longer filters by `hidden` for any role, so pickers must filter themselves. */
  getBanks(): Observable<BanksResponse> {
    return this.networkHelper.get(`${this.hostUrl}/bank`);
  }

  /**
   * Resolve an Armenian domestic account / AM IBAN to bank name + SWIFT via CBA
   * prefix. `bank` is null when the prefix is unknown or the number is too short.
   */
  resolveArmenianBank(accountNumber: string): Observable<{
    bank: { code: string; name: string; swift: string } | null;
  }> {
    return this.networkHelper.get(`${this.hostUrl}/bank/resolve-armenian`, {
      accountNumber,
    });
  }

  /**
   * Resolve a SWIFT/BIC code to bank name + country.
   * `bank` is null when the code is unknown.
   */
  resolveSwiftBank(swift: string): Observable<{
    bank: {
      swift: string;
      name: string;
      countryCode: string;
      city: string | null;
      branch: string | null;
    } | null;
  }> {
    return this.networkHelper.get(`${this.hostUrl}/bank/resolve-swift`, {
      swift,
    });
  }

  createBank(bankData: BankWritePayload): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/bank`, bankData);
  }

  updateBank(id: string, bankData: BankWritePayload): Observable<any> {
    return this.networkHelper.put(`${this.hostUrl}/bank/${id}`, bankData);
  }

  deleteBank(id: string): Observable<any> {
    return this.networkHelper.delete(`${this.hostUrl}/bank/${id}`, {});
  }

  /** Batch instrument entities (incl. derived `bondAci`) by internal id. */
  getInstrumentsByInternalIds(
    instrumentInternalIds: string[],
    aciSettlementDays?: number
  ): Observable<any> {
    const params: Record<string, string | number> = {
      instrumentInternalIds: instrumentInternalIds.join(','),
    };
    if (aciSettlementDays != null) {
      params['aciSettlementDays'] = aciSettlementDays;
    }
    return this.networkHelper.get(`${this.hostUrl}/instruments`, params);
  }

  getRatesByInternalIds(
    instrumentInternalIds: string[]
  ): Observable<Record<string, any>> {
    return this.networkHelper.post(`${this.hostUrl}/instruments/rates`, {
      instrumentInternalIds,
    });
  }

  // FxRates (exchange rates) API methods
  getActiveRates(params?: { search?: string }): Observable<any> {
    const query = params?.search ? { search: params.search } : {};
    return this.networkHelper.get(`${this.hostUrl}/fxrates`, query);
  }

  setRate(data: {
    base: string;
    quote: string;
    ask: number;
    bid: number;
  }): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/fxrates`, data);
  }

  removeRate(id: string): Observable<any> {
    return this.networkHelper.delete(`${this.hostUrl}/fxrates/${id}`, {});
  }

  // Manual instrument rates (any asset class; bonds are % of par)
  getInstrumentRates(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/instrument-rates`, {});
  }

  setInstrumentRate(data: {
    instrumentInternalId: string;
    price: number;
    asOf?: string | number | Date;
  }): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/instrument-rates`, data);
  }

  removeInstrumentRate(id: string): Observable<any> {
    return this.networkHelper.delete(
      `${this.hostUrl}/instrument-rates/${id}`,
      {}
    );
  }

  /** `folder` defaults to `icons` on the backend; `push-notifications` only takes jpg/png up to 1 MB. */
  presignPublicUpload(
    fileName: string,
    folder?: 'icons' | 'push-notifications'
  ): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/s3/presign-upload`, {
      fileName,
      ...(folder && { folder }),
    });
  }

  getUserDocuments(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/user/${userId}/documents`
    );
  }

  addUserDocument(
    userId: string,
    data: {
      key: string;
      fileName: string;
      documentType: string;
      comment?: string;
    }
  ): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/user/${userId}/documents`,
      data
    );
  }

  deleteUserDocument(userId: string, documentId: string): Observable<any> {
    return this.networkHelper.delete(
      `${this.hostUrl}/admin/user/${userId}/documents/${documentId}`,
      {}
    );
  }

  getUserComments(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/user/${userId}/comments`
    );
  }

  addUserComment(userId: string, text: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/user/${userId}/comments`,
      { text }
    );
  }

  getDashboardStats(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/admin/dashboard/stats`, {});
  }

  getSystemFee(): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/admin/fee-management`, {});
  }

  updateSystemFee(data: any): Observable<any> {
    return this.networkHelper.put(`${this.hostUrl}/admin/fee-management`, data);
  }

  getUserFee(userId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/fee-management/${userId}`,
      {}
    );
  }

  updateUserFee(userId: string, data: any): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/fee-management/${userId}`,
      data
    );
  }

  deleteUserFee(userId: string): Observable<any> {
    return this.networkHelper.delete(
      `${this.hostUrl}/admin/fee-management/${userId}`,
      {}
    );
  }

  /** Global configs when `userId` is omitted, otherwise that user's configs. */
  getCustomFees(userId?: string | null): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/custom-fees`,
      userId ? { userId } : {}
    );
  }

  createCustomFee(data: any): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/admin/custom-fees`, data);
  }

  updateCustomFee(id: string, data: any): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/custom-fees/${id}`,
      data
    );
  }

  deleteCustomFee(id: string): Observable<any> {
    return this.networkHelper.delete(
      `${this.hostUrl}/admin/custom-fees/${id}`,
      {}
    );
  }

  getGlobalManagerFee(): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/manager-fee-management`,
      {}
    );
  }

  updateGlobalManagerFee(data: any): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/manager-fee-management`,
      data
    );
  }

  getManagerFee(managerId: string): Observable<any> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/manager-fee-management/${managerId}`,
      {}
    );
  }

  updateManagerFee(managerId: string, data: any): Observable<any> {
    return this.networkHelper.put(
      `${this.hostUrl}/admin/manager-fee-management/${managerId}`,
      data
    );
  }

  deleteManagerFee(managerId: string): Observable<any> {
    return this.networkHelper.delete(
      `${this.hostUrl}/admin/manager-fee-management/${managerId}`,
      {}
    );
  }

  /**
   * The original one-shot importer. **Nothing in the panel calls this any
   * more** — the holdings screen goes through
   * {@link OneCBalanceReconciliationService} instead.
   *
   * It skips any currency the user already has a Balance row for, and the skip
   * is existence-based: a row sitting at zero suppresses the import forever. So
   * for any client who has ever traded it quietly does nothing, which is why
   * the button that used to call it was moved to the reconciliation flow. Kept
   * because the route still exists; think hard before wiring a button to it.
   */
  import1CBalances(userId: string): Observable<any> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/1c/balances/${userId}`,
      {}
    );
  }

  /**
   * Re-pull the user's exchange accounts from GTN and upsert them. Accounts GTN
   * no longer returns are kept. Resolves to `{ gtnExchangeAccount }`.
   */
  resyncGtnExchangeAccounts(
    userId: string
  ): Observable<{ gtnExchangeAccount: any[] }> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/user/${userId}/gtn/resync-exchange-accounts`,
      {}
    );
  }

  getSyncLogs1C(params: {
    page: number;
    itemsPerPage: number;
    status: string;
    type: string;
    startDate?: number;
    endDate?: number;
  }): Observable<{ items: any[]; total: number }> {
    const query: any = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
      status: params.status,
      type: params.type,
    };
    if (params.startDate) query.startDate = params.startDate;
    if (params.endDate) query.endDate = params.endDate;
    return this.networkHelper.get(`${this.hostUrl}/admin/sync-logs/1c`, query);
  }

  getSyncLogsFx(params: {
    page: number;
    itemsPerPage: number;
    status: string;
    startDate?: number;
    endDate?: number;
  }): Observable<{ items: any[]; total: number }> {
    const query: any = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
      status: params.status,
    };
    if (params.startDate) query.startDate = params.startDate;
    if (params.endDate) query.endDate = params.endDate;
    return this.networkHelper.get(`${this.hostUrl}/admin/sync-logs/fx`, query);
  }

  ekengSearchByDocNum(docNum: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/ekeng/search-by-doc-num`, {
      docNum,
    });
  }

  ekengSearchByPsn(pNum: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/ekeng/search-by-psn`, {
      pNum,
    });
  }

  ekengGetTaxSsnInfo(
    ssn?: string,
    start_date?: string,
    end_date?: string
  ): Observable<any> {
    const body: any = {};
    if (ssn) body.ssn = ssn;
    if (start_date) body.start_date = start_date;
    if (end_date) body.end_date = end_date;
    return this.networkHelper.post(`${this.hostUrl}/ekeng/tax-ssn-info`, body);
  }

  getEkengLogs(params: {
    page: number;
    itemsPerPage: number;
    type?: string;
    startDate?: number;
    endDate?: number;
  }): Observable<{ items: any[]; total: number }> {
    const query: any = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
      type: params.type || 'any',
    };
    if (params.startDate) query.startDate = params.startDate;
    if (params.endDate) query.endDate = params.endDate;
    return this.networkHelper.get(`${this.hostUrl}/ekeng/logs`, query);
  }

  getUserEkengData(userId: string): Observable<any> {
    return this.networkHelper.get(`${this.hostUrl}/ekeng/user/${userId}`);
  }

  refreshUserEkengData(userId: string): Observable<any> {
    return this.networkHelper.post(`${this.hostUrl}/ekeng/user/${userId}`, {});
  }
}
